import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  useTheme,
  Divider,
  Chip
} from '@mui/material';
import { PlayArrow, Stop, Public, Info as InfoIcon } from '@mui/icons-material';
import { useActivity } from '../../contexts/ActivityContext';

const TracerouteVisualizer = () => {
  const [target, setTarget] = useState('google.com');
  const [isTracing, setIsTracing] = useState(false);
  const [hops, setHops] = useState([]);
  const [error, setError] = useState(null);
  const { updateActivity } = useActivity();
  const theme = useTheme();

  const simulateTraceroute = async () => {
    if (!target) {
      setError('Please enter a valid hostname or IP');
      return;
    }

    setIsTracing(true);
    setError(null);
    setHops([]);

    // Simulated traceroute data (in a real app, this would be an API call)
    const mockHops = [
      { hop: 1, ip: '192.168.1.1', hostname: 'router.local', rtt: '5.23', location: 'Local Network' },
      { hop: 2, ip: '100.64.0.1', hostname: 'isp-gateway.example.net', rtt: '12.45', location: 'ISP Network' },
      { hop: 3, ip: '203.0.113.1', hostname: 'core-router1.isp.net', rtt: '15.67', location: 'ISP Backbone' },
      { hop: 4, ip: '198.51.100.1', hostname: 'border-router.isp.net', rtt: '18.90', location: 'ISP Border' },
      { hop: 5, ip: '203.0.113.45', hostname: 'peering-router1.ixp.net', rtt: '22.10', location: 'Internet Exchange' },
      { hop: 6, ip: '198.51.100.67', hostname: 'google-edge1.ixp.net', rtt: '25.30', location: 'Google Network' },
      { hop: 7, ip: '172.217.0.142', hostname: 'google.com', rtt: '28.50', location: 'Mountain View, CA, US' },
    ];

    // Simulate the traceroute with delays
    for (let i = 0; i < mockHops.length; i++) {
      if (!isTracing) break;
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setHops(prev => [...prev, mockHops[i]]);
    }

    if (isTracing) {
      updateActivity('Traceroute', `Completed trace to ${target}`, { 
        hopCount: mockHops.length,
        finalRTT: mockHops[mockHops.length - 1].rtt
      });
    }
    
    setIsTracing(false);
  };

  const handleStartTrace = () => {
    simulateTraceroute();
  };

  const handleStopTrace = () => {
    setIsTracing(false);
  };

  const getStatusColor = (rtt) => {
    const rttValue = parseFloat(rtt);
    if (rttValue < 50) return 'success';
    if (rttValue < 150) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Traceroute
              <Tooltip title="Trace the network path to a host">
                <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </Tooltip>
            </Typography>
            {!isTracing ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrow />}
                onClick={handleStartTrace}
                disabled={isTracing}
              >
                Start Trace
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Stop />}
                onClick={handleStopTrace}
              >
                Stop
              </Button>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Target Hostname or IP"
              variant="outlined"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={isTracing}
              size="small"
              sx={{ mb: 1 }}
            />
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>

          {isTracing && (
            <Box sx={{ width: '100%', mb: 3 }}>
              <LinearProgress />
              <Typography variant="caption" color="textSecondary">
                Tracing route to {target}...
              </Typography>
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hop</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Hostname</TableCell>
                  <TableCell align="right">Response Time</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hops.map((hop, index) => (
                  <TableRow key={index}>
                    <TableCell>{hop.hop}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Public fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                        {hop.ip}
                      </Box>
                    </TableCell>
                    <TableCell>{hop.hostname}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${hop.rtt} ms`} 
                        size="small"
                        color={getStatusColor(hop.rtt)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={hop.location}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!hops.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      {isTracing 
                        ? 'Tracing route...' 
                        : 'Enter a hostname or IP and click Start Trace to begin'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">
              Note: This is a simulation. In a production environment, this would perform actual network requests.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TracerouteVisualizer;
